import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CircularProgress } from '@material-ui/core';
import { useHistory, useParams } from 'react-router-dom';
import { httpClient } from '../utils/dataProvider';
import { groupReportLogs, summarizeReportGroups } from './dashboardAnalytics';
import { fetchAllReportLogs } from './reportLogs';

const API = 'https://skilzapp.net/api';

const TABS = [
  { key: 'all', label: 'All Reports', detail: 'Every report group', tone: 'green' },
  { key: 'requests', label: 'Request Reports', detail: 'Service requests', tone: 'orange' },
  { key: 'users', label: 'User Reports', detail: 'Member accounts', tone: 'blue' },
  { key: 'feeds', label: 'Feed Reports', detail: 'Community posts', tone: 'purple' },
];

const Icon = ({ type }) => {
  const paths = {
    users: 'M16 19v-1.5a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4V19M9.5 10a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM16 8h5M18.5 5.5v5',
    reports: 'M12 8v4m0 4h.01M10.3 4.2 2.7 18a2 2 0 0 0 1.75 3h15.1a2 2 0 0 0 1.75-3L13.7 4.2a2 2 0 0 0-3.4 0Z',
  };
  return <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d={paths[type] || paths.reports} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>;
};

const relativeTime = value => {
  const elapsed = Date.now() - new Date(value).getTime();
  if (!Number.isFinite(elapsed) || elapsed < 0) return new Date(value).toLocaleString();
  const minutes = Math.floor(elapsed / 60000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
};

const typeLabel = type => {
  if (type === 'requests') return 'Request';
  if (type === 'users') return 'User';
  if (type === 'feeds') return 'Feed';
  return type || 'Unknown';
};

const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logs, setLogs] = useState([]);
  const [mutationLoading, setMutationLoading] = useState(false);
  const history = useHistory();
  const { tab } = useParams();

  const activeTab = ['requests', 'users', 'feeds'].includes(tab) ? tab : 'all';

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setLogs(await fetchAllReportLogs());
    } catch (err) {
      console.error('Failed to load reports:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const reportGroups = useMemo(() => groupReportLogs(logs), [logs]);
  const reportSummary = useMemo(() => summarizeReportGroups(reportGroups), [reportGroups]);

  const filteredGroups = useMemo(() => {
    if (activeTab === 'all') return reportGroups;
    return reportGroups.filter(g => g.relatedType === activeTab);
  }, [reportGroups, activeTab]);

  const openGroups = useMemo(() => filteredGroups.filter(g => g.groupStatus === 'OPEN'), [filteredGroups]);
  const resolvedGroups = useMemo(() => filteredGroups.filter(g => g.groupStatus === 'DONE'), [filteredGroups]);

  const handleTabClick = key => {
    const path = key === 'all' ? '/reports' : `/reports/${key}`;
    history.push(path);
  };

  const handleTabKeyDown = (key, event) => {
    const currentIndex = TABS.findIndex(item => item.key === key);
    let nextIndex = currentIndex;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % TABS.length;
    else if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + TABS.length) % TABS.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = TABS.length - 1;
    else return;

    event.preventDefault();
    const nextKey = TABS[nextIndex].key;
    handleTabClick(nextKey);
    requestAnimationFrame(() => document.getElementById(`reports-tab-${nextKey}`)?.focus());
  };

  const toggleGroup = async (group, targetStatus) => {
    setMutationLoading(true);
    try {
      await Promise.all(
        group.logs
          .filter(log => log.status !== targetStatus)
          .map(log => httpClient(`${API}/logs/${log._id}`, {
            method: 'PATCH',
            body: JSON.stringify({ status: targetStatus }),
          }))
      );
      await fetchLogs();
    } catch (err) {
      console.error(err);
      window.alert('Report status could not be updated.');
    } finally {
      setMutationLoading(false);
    }
  };

  const tabSummary = key => {
    if (key === 'all') {
      const total = Object.values(reportSummary).reduce((s, v) => s + v.total, 0);
      const open = Object.values(reportSummary).reduce((s, v) => s + v.open, 0);
      return { total, open };
    }
    return reportSummary[key] || { total: 0, open: 0 };
  };

  if (loading) {
    return (
      <div className="sk-reports" id="main-content">
        <section className="sk-reports__header">
          <div><div className="sk-reports__eyebrow">Moderation</div><h1>Reports</h1><p>Review and resolve reported content across requests, users, and feeds.</p></div>
        </section>
        <div className="sk-reports__loading" role="status" aria-label="Loading reports">
          <CircularProgress size={36} style={{ color: 'var(--sk-green)' }} />
          <span>Loading reports…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sk-reports" id="main-content">
        <section className="sk-reports__header">
          <div><div className="sk-reports__eyebrow">Moderation</div><h1>Reports</h1><p>Review and resolve reported content across requests, users, and feeds.</p></div>
        </section>
        <div className="sk-reports__error" role="alert">
          <p>Reports could not be loaded.</p>
          <button className="sk-reports__retry-btn" type="button" onClick={fetchLogs}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="sk-reports" id="main-content">
      <section className="sk-reports__header">
        <div><div className="sk-reports__eyebrow">Moderation</div><h1>Reports</h1><p>Review and resolve reported content across requests, users, and feeds.</p></div>
      </section>

      <nav className="sk-reports__tabs" role="tablist" aria-label="Report categories">
        {TABS.map(tab => {
          const summary = tabSummary(tab.key);
          const isActive = activeTab === tab.key;
          const groupLabel = summary.total === 1 ? 'group' : 'groups';
          return (
            <button
              key={tab.key}
              id={`reports-tab-${tab.key}`}
              role="tab"
              type="button"
              aria-label={`${tab.label}, ${summary.total} ${groupLabel}, ${summary.open} open`}
              aria-selected={isActive}
              aria-controls="reports-panel"
              tabIndex={isActive ? 0 : -1}
              className={`sk-reports__tab ${isActive ? 'active' : ''} tone-${tab.tone}`}
              onClick={() => handleTabClick(tab.key)}
              onKeyDown={event => handleTabKeyDown(tab.key, event)}
            >
              <span className="sk-reports__tab-topline">
                <span className="sk-reports__tab-icon"><Icon type={tab.key === 'users' ? 'users' : 'reports'} /></span>
                <span className="sk-reports__tab-copy">
                  <span className="sk-reports__tab-label">{tab.label}</span>
                  <span className="sk-reports__tab-detail">{tab.detail}</span>
                </span>
              </span>
              <span className="sk-reports__tab-meta">
                <span className="sk-reports__tab-total"><strong>{summary.total}</strong> {groupLabel}</span>
                <span className="sk-reports__tab-open">{summary.open} open</span>
              </span>
            </button>
          );
        })}
      </nav>

      <section
        className="sk-reports__content"
        id="reports-panel"
        role="tabpanel"
        aria-labelledby={`reports-tab-${activeTab}`}
      >
        <div className="sk-reports__section">
          <h2 className="sk-reports__section-title">
            <span className="sk-reports__section-icon open" />
            Unresolved Reports
            <span className="sk-reports__section-count">{openGroups.length}</span>
          </h2>
          {openGroups.length === 0 ? (
            <p className="sk-reports__empty">No unresolved reports</p>
          ) : (
            <div className="sk-reports__grid">
              {openGroups.map(group => (
                <ReportGroupCard key={`${group.relatedType}-${group.relatedId}`} group={group} mutationLoading={mutationLoading} onToggle={toggleGroup} />
              ))}
            </div>
          )}
        </div>

        <div className="sk-reports__section">
          <h2 className="sk-reports__section-title">
            <span className="sk-reports__section-icon resolved" />
            Resolved Reports
            <span className="sk-reports__section-count">{resolvedGroups.length}</span>
          </h2>
          {resolvedGroups.length === 0 ? (
            <p className="sk-reports__empty">No resolved reports yet</p>
          ) : (
            <div className="sk-reports__grid">
              {resolvedGroups.map(group => (
                <ReportGroupCard key={`${group.relatedType}-${group.relatedId}`} group={group} mutationLoading={mutationLoading} onToggle={toggleGroup} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const ReportGroupCard = ({ group, mutationLoading, onToggle }) => {
  const isOpen = group.groupStatus === 'OPEN';
  const toneClass = group.relatedType === 'users' ? 'blue' : group.relatedType === 'feeds' ? 'purple' : 'orange';

  return (
    <Card className="sk-reports__card">
      <CardContent className="sk-reports__card-content">
        <div className="sk-reports__card-top">
          <span className={`sk-reports__card-icon ${toneClass}`}>
            <Icon type={group.relatedType === 'users' ? 'users' : 'reports'} />
          </span>
          <div className="sk-reports__card-info">
            <strong>{typeLabel(group.relatedType)}</strong>
            <small className="sk-reports__card-id">{group.relatedId?.slice(-6) || 'Unknown'}</small>
          </div>
          <span className={`sk-reports__status-badge ${isOpen ? 'open' : 'resolved'}`}>
            {isOpen ? 'Open' : 'Resolved'}
          </span>
        </div>

        <div className="sk-reports__card-stats">
          <span>{group.total} report{group.total === 1 ? '' : 's'}</span>
          {isOpen && group.openCount > 0 && <span className="sk-reports__open-count">{group.openCount} open</span>}
          {group.latest && <time className="sk-reports__card-time">{relativeTime(group.latest.createdAt)}</time>}
        </div>

        <div className="sk-reports__card-actions">
          {group.relatedId !== 'UNKNOWN' && (
            <a className="sk-reports__view-btn" href={`#/${group.relatedType}/${group.relatedId}/show`}>View</a>
          )}
          <button
            className={`sk-reports__toggle-btn ${isOpen ? 'resolve' : 'reopen'}`}
            type="button"
            disabled={mutationLoading}
            onClick={() => onToggle(group, isOpen ? 'DONE' : 'OPEN')}
          >
            {isOpen ? 'Mark Resolved' : 'Reopen'}
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

export default Reports;
